/*
 * The three rows of a first minute (VISUAL.md §7.16, D-10 §2.3): where the team is (*Open
 * #allgemein*), who is here (*See people*, behind the first five faces of the directory), and where
 * one's own name and picture live (*Complete profile*). Each row ends in a door rather than a promise.
 *
 * A team member and a guest are greeted by the same three rows inside their own card
 * (`FirstRunMember.tsx`, `FirstRunGuest.tsx`); what differs is what the first row says when there is
 * no channel to open yet, because "channels show up as soon as you join one" is a promise a guest's
 * shell cannot keep — a guest joins only what their groups own or what somebody adds them to. So the
 * card hands that sentence in, and everything else is said once, here.
 *
 * Every door dismisses on the way out: a person who opened the channel has been greeted, and coming
 * back to Home to press *Got it* would be a chore. The channel door is handed in rather than looked
 * up: which conversation is worth opening first is a question about the reader's own places, and
 * `routes/frames.tsx` — the screen that already answers it for the landing redirect — is where that
 * answer is made.
 */

import { Link } from "@tanstack/react-router"
import { conversationHref } from "../../routes/links.ts"
import { Button } from "../../ui/index.ts"
import { t, translate, type PlainKey } from "../i18n/index.ts"
import { useSlot } from "../modules/slots.ts"
import { byName } from "../people/person.ts"
import { PersonFace, chipPerson } from "../people/PersonChip.tsx"
import type { Model } from "../store/model.ts"
import { useModel } from "./context.tsx"
import { focusComposerWhenItArrives } from "./layout.ts"
import type { PlaceDoor } from "./SetupChecklist.tsx"

/** How many of the directory's faces the "who is here" row shows; the count beside them is real. */
const FACES = 5

/** The conversation behind a door, found by the address the door carries. */
function placeOf(model: Model, channel: PlaceDoor | undefined) {
	if (channel === undefined) return undefined
	return Object.values(model.conversations).find(
		(slice) => slice.meta.slug !== undefined && channel.href === conversationHref(slice.meta),
	)
}

/** The people a newcomer is introduced to: everybody who can sign in, by name, the bot left out. */
function peopleHere(users: Model["users"]) {
	return byName(Object.values(users).filter((user) => user.kind !== "bot"))
}

export function FirstRunRows({
	channel,
	whereNoneKey,
	onDismiss,
}: {
	channel: PlaceDoor | undefined
	/** What the first row says while the reader is in no channel yet. */
	whereNoneKey: PlainKey
	/** Marks the greeting as seen; every door calls it on the way out. */
	onDismiss: () => void
}) {
	const people = peopleHere(useModel((model) => model.users))
	// Two primitives about the door's channel, so the row repaints when one of them moves.
	const members = useModel((model) => placeOf(model, channel)?.meta.memberIds?.length ?? 0)
	const topic = useModel((model) => placeOf(model, channel)?.meta.topic ?? "")

	// The door of last resort for the first row: the module's primary Home action, which is chat's
	// *Browse channels*. Core asks the slot rather than naming the dialog, the way Home's empty state
	// does — it may not import a module (architecture.md D21).
	const actions = useSlot("homeActions")
	const door = actions.find((action) => action.primary === true) ?? actions[0]

	return (
		<dl className="tb-firstrun__rows">
			<div className="tb-firstrun__row">
				<dt>{t("shell.firstRun.where")}</dt>
				<dd className="tb-firstrun__detail">
					{channel === undefined ? (
						t(whereNoneKey)
					) : (
						<span className="tb-firstrun__line">
							{t("shell.firstRun.whereLine", {
								channel: channel.label,
								members: t("shell.firstRun.peopleCount", { count: members }),
							})}
							{topic === "" ? null : ` · “${topic}”`}
						</span>
					)}
				</dd>
				<dd className="tb-firstrun__door">
					{channel === undefined ? (
						door === undefined ? null : (
							// Nothing to open means nothing this reader is in yet, and a row of the first
							// minute that ends in no control at all is a promise rather than a door
							// (N5-R1-4): the module's own way to the places that *do* exist — *Browse
							// channels* — stands here instead, as the quieter variant, because on this
							// instance the loud first step is somebody else's to take.
							<Button
								variant="secondary"
								label={translate(door.labelKey)}
								onClick={() => {
									onDismiss()
									door.onSelect()
								}}
							/>
						)
					) : (
						<Link
							to={channel.href}
							className="tb-button tb-button--primary"
							onClick={() => {
								onDismiss()
								focusComposerWhenItArrives()
							}}
						>
							{t("shell.home.open", { channel: channel.label })}
						</Link>
					)}
				</dd>
			</div>

			<div className="tb-firstrun__row">
				<dt>{t("shell.firstRun.who")}</dt>
				<dd className="tb-firstrun__detail">
					<span className="tb-faces tb-firstrun__faces">
						{people.slice(0, FACES).map((user) => (
							<PersonFace key={user.id} person={chipPerson(user)} hover={false} />
						))}
					</span>
					{t("shell.firstRun.peopleCount", { count: people.length })}
				</dd>
				<dd className="tb-firstrun__door">
					<Link to="/people" className="tb-button tb-button--secondary" onClick={onDismiss}>
						{t("shell.firstRun.people")}
					</Link>
				</dd>
			</div>

			<div className="tb-firstrun__row">
				<dt>{t("shell.firstRun.say")}</dt>
				<dd className="tb-firstrun__detail">{t("shell.firstRun.sayDetail")}</dd>
				<dd className="tb-firstrun__door">
					<Link
						to="/settings/profile"
						className="tb-button tb-button--secondary"
						onClick={onDismiss}
					>
						{t("shell.firstRun.profile")}
					</Link>
				</dd>
			</div>
		</dl>
	)
}
